import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { API } from "@/lib/site";
import { useCart } from "@/context/CartContext";

/** Return target for Stripe Checkout. Polls the session status — the webhook
 * or this poll (whichever first) flips the order to paid server-side. */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState({ phase: "checking", reference: null });
  const ticks = useRef(0);

  useEffect(() => {
    document.title = "Payment received | Regal Tuning";
    if (!sessionId) {
      setState({ phase: "failed" });
      return;
    }
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/stripe/status/${sessionId}`);
        if (!alive) return;
        if (data.payment_status === "paid") {
          setState({ phase: "paid", reference: data.reference });
          clear();
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setState({ phase: "failed" });
          return;
        }
      } catch { /* keep polling briefly */ }
      if (!alive) return;
      ticks.current += 1;
      if (ticks.current < 15) setTimeout(poll, 2000);
      else setState({ phase: "timeout" });
    };
    poll();
    return () => { alive = false; };
  }, [sessionId, clear]);

  return (
    <main className="flex min-h-[70vh] items-center justify-center bg-surface px-5" data-testid="payment-success-page">
      <div className="w-full max-w-md border border-line bg-white px-8 py-12 text-center">
        {state.phase === "checking" && (
          <>
            <Loader2 size={44} className="mx-auto animate-spin text-regal-amber" />
            <p className="mt-5 font-display text-lg font-bold uppercase tracking-wide text-ink">Confirming your payment…</p>
            <p className="mt-2 text-sm text-neutral-500">This takes a few seconds.</p>
          </>
        )}
        {state.phase === "paid" && (
          <>
            <CheckCircle2 size={52} className="mx-auto text-regal-amber" />
            <p className="mt-5 font-display text-xl font-bold uppercase tracking-wide text-ink">Payment received</p>
            <p className="mt-2 text-sm text-neutral-500">
              Order reference <span className="font-mono text-regal-amber" data-testid="paid-order-reference">{state.reference}</span>
            </p>
            <p className="mt-1 text-sm text-neutral-500">A confirmation email is on its way.</p>
            <Link to="/tuning-tools" data-testid="paid-continue-link"
              className="mt-8 inline-block border border-regal-amber px-8 py-3.5 font-display text-xs font-bold uppercase tracking-[0.16em] text-regal-amber transition-colors hover:bg-regal-amber hover:text-void">
              Continue shopping
            </Link>
          </>
        )}
        {state.phase === "failed" && (
          <>
            <XCircle size={52} className="mx-auto text-red-500" />
            <p className="mt-5 font-display text-xl font-bold uppercase tracking-wide text-ink">Payment not completed</p>
            <p className="mt-2 text-sm text-neutral-500">No charge was made. Your cart is untouched — try again or choose another payment method.</p>
            <Link to="/tuning-tools" data-testid="failed-retry-link"
              className="mt-8 inline-block border border-regal-amber px-8 py-3.5 font-display text-xs font-bold uppercase tracking-[0.16em] text-regal-amber transition-colors hover:bg-regal-amber hover:text-void">
              Back to the shop
            </Link>
          </>
        )}
        {state.phase === "timeout" && (
          <>
            <Loader2 size={44} className="mx-auto text-regal-amber" />
            <p className="mt-5 font-display text-lg font-bold uppercase tracking-wide text-ink">Still confirming…</p>
            <p className="mt-2 text-sm text-neutral-500">Your payment went through but confirmation is slow. Check your email — or contact us and we&apos;ll confirm manually.</p>
            <Link to="/contact" data-testid="timeout-contact-link" className="mt-8 inline-block border border-regal-amber px-8 py-3.5 font-display text-xs font-bold uppercase tracking-[0.16em] text-regal-amber transition-colors hover:bg-regal-amber hover:text-void">
              Contact us
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
